import { allowed_all_hmac_key_names, white_list_outgoing_person_fields } from '../helpers/static_vars';
import { normalize_data_hub_privacy_policy_string } from '../helpers/data_hub_privacy_policy';
// state.js is deliberately import-light (no privacy import), so this cannot cycle.
import { cleanup_analytics_state } from './state';

// Sources the personal-data and auth allowlists from shared-functions/static_vars and adds the
// common auth/session params PostHog and amplitude also strip.
const sensitive_url_parameter_names = new Set([
	...allowed_all_hmac_key_names,
	...white_list_outgoing_person_fields,
	'token',
	'signature',
	'auth',
	'code',
	'state',
	'session',
	'jwt',
	'email',
	'phone',
	'address'
]);

export function is_sensitive_url_parameter(parameter_name) {
	if (!parameter_name || typeof parameter_name !== 'string') {
		return false;
	}
	const normalized_parameter_name = parameter_name.trim().toLowerCase();
	return normalized_parameter_name.startsWith('h_') || normalized_parameter_name.startsWith('hmac_')
		|| sensitive_url_parameter_names.has(normalized_parameter_name)
		|| normalized_parameter_name.includes('email')
		|| normalized_parameter_name.includes('phone')
		|| normalized_parameter_name.includes('address');
}

export function scrub_url_string(url_string) {
	if (!url_string || typeof url_string !== 'string') {
		return url_string || null;
	}
	try {
		const parsed_url = new URL(url_string, window.location.origin);
		Array.from(parsed_url.searchParams.keys()).forEach((parameter_name) => {
			if (is_sensitive_url_parameter(parameter_name)) {
				parsed_url.searchParams.delete(parameter_name);
			}
		});
		return parsed_url.toString();
	} catch {
		return url_string.split('?')[0];
	}
}

// CSS selectors for elements (or ancestors) that should skip auto capture ($click, $video, etc.).
// Includes DataHub-native markers plus common third-party opt-out attributes and classes.
const do_not_track_capture_css_selectors = [
	'[data-hub-do-not-track="true"]',
	'[data-ph-no-capture]',
	'[data-posthog-no-capture]',
	'[data-ga-ignore]',
	'[data-amplitude-ignore]',
	'.ph-no-capture',
	'.fs-exclude',
	'.fs-block'
].join(',');

/**
 * Returns true when the element or any ancestor is marked to skip auto analytics capture.
 * Honors DataHub `data-hub-do-not-track`, third-party data-attribute aliases, and class-based
 * opt-outs such as PostHog's `ph-no-capture` and FullStory's `fs-exclude`.
 * @param {Element|null|undefined} element
 * @returns {boolean}
 */
export function element_or_parent_has_do_not_track_attribute(element) {
	if (!element || typeof element.closest !== 'function') {
		return false;
	}
	return Boolean(element.closest(do_not_track_capture_css_selectors));
}

export function is_sensitive_text_surface(element) {
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return false;
	}
	const tag_name = element.tagName?.toLowerCase();
	const input_type = element.getAttribute?.('type')?.toLowerCase();
	return tag_name === 'textarea'
		|| tag_name === 'select'
		|| tag_name === 'input'
		|| input_type === 'password'
		|| input_type === 'email'
		|| input_type === 'tel'
		|| input_type === 'hidden'
		|| element.isContentEditable
		|| Boolean(element.closest?.('[contenteditable="true"]'));
}

/**
 * Persistent (cross-session) Do-Not-Track flag, scoped per organization_id. localStorage is
 * per-origin, so a flag set on dailykos.com only suppresses analytics on dailykos.com — and
 * the org_id suffix means a user who opted out under one organization on a shared domain
 * isn't auto-opted-out under a different organization. Falls back to a 'shared' bucket
 * when no organization_id is available (matches the session_storage_key pattern in
 * session.js). We use localStorage rather than a cookie because (a) no SameSite/third-party
 * cookie restrictions, (b) JS-only consumer, (c) persists until the user clears site data.
 * forget_user writes this flag so "forget me" stays sticky across reloads — captures_disabled
 * alone is per-instance and resets on reload.
 */
const persistent_do_not_track_localstorage_key_prefix = 'data-hub:do-not-track';

function get_persistent_do_not_track_localstorage_key(data_hub_instance) {
	const organization_id = typeof data_hub_instance?.organization_id === 'string' && data_hub_instance.organization_id.trim()
		? data_hub_instance.organization_id.trim()
		: 'shared';
	return `${persistent_do_not_track_localstorage_key_prefix}:${organization_id}`;
}

/**
 * Reads the persistent DNT flag for the org-scoped key. Returns true if a prior forget_user
 * call (or the host site's consent banner) wrote it. Safe in non-browser environments —
 * returns false when localStorage is unavailable.
 * @param {Object|null|undefined} data_hub_instance
 * @returns {boolean}
 */
export function read_persistent_do_not_track_flag(data_hub_instance = window?.data_hub) {
	try {
		if (typeof localStorage !== 'undefined') {
			return localStorage.getItem(get_persistent_do_not_track_localstorage_key(data_hub_instance)) === 'true';
		}
	} catch {
		// Storage may be blocked in private mode or by browser settings.
	}
	return false;
}

/**
 * Writes (or removes) the persistent DNT flag for the org-scoped key. Pass true to opt out,
 * false to opt back in. Opting back in does NOT re-enable analytics in the current instance —
 * captures stay disabled until the next page reload, because the analytics module already
 * tore down its listeners and queues.
 * @param {Object|null|undefined} data_hub_instance
 * @param {boolean} should_do_not_track
 * @returns {void}
 */
export function write_persistent_do_not_track_flag(data_hub_instance, should_do_not_track) {
	try {
		if (typeof localStorage === 'undefined') {
			return;
		}
		const storage_key = get_persistent_do_not_track_localstorage_key(data_hub_instance);
		if (should_do_not_track) {
			localStorage.setItem(storage_key, 'true');
		} else {
			localStorage.removeItem(storage_key);
		}
	} catch {
		// Storage may be blocked.
	}
}

/**
 * Server-driven forgotten state (CCPA erasure): when any data-hub-action
 * response carries a person with a non-empty `forgotten_at`, the person was
 * erased/suppressed server-side — quick-exit tracking NOW (cleanup) and set
 * the persistent org-scoped DNT flag so no analytics session starts on
 * future loads.
 *
 * The flag is DELIBERATELY sticky: a later server-side unforget does not
 * clear it (client-side DNT is an independent privacy layer on top of the
 * server suppression — same contract as forget_user, see
 * docs/forget-user-and-privacy.md). Re-enabling requires
 * write_persistent_do_not_track_flag(instance, false) + reload.
 *
 * @param {Object|null|undefined} data_hub_instance
 * @param {Object|null|undefined} person_payload - server person/user_data object
 * @returns {boolean} true when the payload was forgotten and DNT was applied
 */
export function apply_server_forgotten_state(data_hub_instance, person_payload) {
	const forgotten_at_value = person_payload?.forgotten_at;
	const is_forgotten = typeof forgotten_at_value === 'string'
		? forgotten_at_value.trim() !== ''
		: Boolean(forgotten_at_value);
	if (!is_forgotten) {
		return false;
	}
	write_persistent_do_not_track_flag(data_hub_instance, true);
	try {
		cleanup_analytics_state(data_hub_instance);
	} catch {
		// Analytics may not be initialized yet; the persistent flag above
		// still blocks the next session at bootstrap.
	}
	return true;
}

export function analytics_consent_allows_capture(data_hub_instance = window?.data_hub) {
	// Org-scoped persistent DNT comes first — set by a prior forget_user, survives reloads,
	// and overrides any in-config or runtime consent grant. To re-enable analytics the host
	// site must call write_persistent_do_not_track_flag(instance, false) AND reload the page.
	if (read_persistent_do_not_track_flag(data_hub_instance)) {
		return false;
	}
	if (typeof window !== 'undefined' && window.data_hub_consent === false) {
		return false;
	}
	const policy = normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy);
	if (policy === 'none') {
		return false;
	}
	const do_not_track_value = navigator?.doNotTrack || window?.doNotTrack || navigator?.msDoNotTrack;
	if (do_not_track_value === '1' || do_not_track_value === 'yes') {
		return false;
	}
	return true;
}
